// Estado de la Sala del médico para pantalla (Sala CRM↔HCE, rebanada 5): carga, refresco silencioso por el canal en vivo (con el respaldo de 30 s de
// `useEventosSala`) y ejecución de acciones con su estado esperado. Lo comparten la página «Sala» y el bloque del Dashboard: es UNA sola lógica.
import { useCallback, useEffect, useRef, useState } from 'react';
import { crearUltimaPeticion } from './ultimaPeticion';
import { useEventosSala } from './useEventosSala';
import { ErrorSala, obtenerSala, type SalaDelDia } from './salaHce';
import type { EstadoEnVivo } from './eventosSala';

export type EstadoSala = {
  sala: SalaDelDia | null;
  cargando: boolean;
  /** Error de la última carga (no de una acción): la Sala sigue mostrando lo último que tenía. */
  errorCarga: boolean;
  /** El servidor respondió 403: esta cuenta no tiene Sala (p. ej. director técnico de farmacia). No es un error: la pantalla no muestra nada. */
  sinSala: boolean;
  enVivo: EstadoEnVivo;
  /** Turnos con una acción en curso (doble clic: el botón queda deshabilitado). */
  ocupados: ReadonlySet<number>;
  /** Lectura del reloj monotónico en el momento en que llegó `sala`: para sumar solo el tiempo transcurrido a la espera. */
  recibidoEnMs: number;
  /** Reloj monotónico actual (se actualiza cada 15 s para refrescar la espera en pantalla). */
  ahoraMs: number;
  recargar: () => Promise<void>;
  /**
   * Corre una acción sobre un turno. Devuelve su resultado, o `null` si falló: el error ya se avisó con `alFallar` y, si el turno había cambiado
   * (409) o ya no existe (404), la Sala se vuelve a pedir sola. Un doble clic mientras la acción está en curso no la repite.
   */
  ejecutar: <R>(ticketId: number, accion: () => Promise<R>, alFallar: (e: ErrorSala) => void) => Promise<R | null>;
};

export const REFRESCO_ESPERA_MS = 15_000;

const reloj = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export function useSalaDelDia(): EstadoSala {
  const [sala, setSala] = useState<SalaDelDia | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [sinSala, setSinSala] = useState(false);
  const [ocupados, setOcupados] = useState<ReadonlySet<number>>(new Set());
  const [recibidoEnMs, setRecibidoEnMs] = useState(0);
  const [ahoraMs, setAhoraMs] = useState(reloj);
  const ultima = useRef(crearUltimaPeticion());
  const enCurso = useRef(new Set<number>());

  // `silencioso`: recarga por el canal en vivo o el respaldo; no parpadea el «Cargando…» ni borra lo que ya se ve.
  const cargar = useCallback(async (silencioso: boolean) => {
    const peticion = ultima.current.nueva();
    if (!silencioso) setCargando(true);
    try {
      const datos = await obtenerSala();
      if (!peticion.vigente()) return;
      setSala(datos);
      setRecibidoEnMs(reloj());
      setAhoraMs(reloj());
      setErrorCarga(false);
    } catch (err) {
      if (peticion.vigente()) {
        if (err instanceof ErrorSala && err.status === 403) setSinSala(true);
        else setErrorCarga(true);
      }
    } finally {
      if (peticion.vigente()) setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar(false);
  }, [cargar]);

  const { estado: enVivo } = useEventosSala(() => void cargar(true), { tipos: ['sala'] });

  useEffect(() => {
    const h = setInterval(() => setAhoraMs(reloj()), REFRESCO_ESPERA_MS);
    return () => clearInterval(h);
  }, []);

  const ejecutar: EstadoSala['ejecutar'] = useCallback(
    async (ticketId, accion, alFallar) => {
      if (enCurso.current.has(ticketId)) return null;
      enCurso.current.add(ticketId);
      setOcupados(new Set(enCurso.current));
      try {
        const resultado = await accion();
        void cargar(true);
        return resultado;
      } catch (err) {
        const e = err instanceof ErrorSala ? err : new ErrorSala('No se pudo completar la acción.', 0, 'error');
        alFallar(e);
        // 409/404: el turno cambió o ya no es de este médico. 0 (tiempo agotado o sin conexión): la acción pudo aplicarse en el servidor aunque la respuesta no llegó.
        if (e.status === 409 || e.status === 404 || e.status === 0) void cargar(true);
        return null;
      } finally {
        enCurso.current.delete(ticketId);
        setOcupados(new Set(enCurso.current));
      }
    },
    [cargar],
  );

  return { sala, cargando, errorCarga, sinSala, enVivo, ocupados, recibidoEnMs, ahoraMs, recargar: () => cargar(true), ejecutar };
}
